import React, { useState } from 'react';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LinkIcon from '@mui/icons-material/LinkOutlined';
import { format } from 'date-fns';
import { getServiceLabel } from '../../helpers/services';
import VirtualizedTable from '../table/VirtualizedTable';
import useGetQueue from '../../hooks/queue/useGetQueue';
import PageLoading from '../global/PageLoading';
import { ActionsData, Columns, DataFromColumns } from '../../types/reports';
import useGetWaivers from '../../hooks/waivers/useGetWaivers';
import { Feature } from '../../types/subscriptions';
import useFeatureGuard from '../../hooks/subscriptions/useFeatureGuard';
import WaiverStatusBadge from '../waivers/WaiverStatusBadge';
import useUser from '../../hooks/global/useUser';
import { Appointment, LoadedUser } from '../../interfaces';
import QueueSettingsFormModal from './QueueSettingsFormModal';
import useRemoveFromQueue from '../../hooks/queue/useRemoveFromQueue';
import useLoadingModal from '../../hooks/global/useLoadingModal';
import useAwaitedForm from '../../hooks/useAwaitedForm';
import { QueueAppointment } from '../../types/queue';
import { isUserAdminOrManager } from '../../utils/global/helper';
import useCheckoutForm from '../../hooks/global/useCheckoutForm';
import { removeFromQueue } from '../../helpers/queue';
import { getSelectedArtistsText } from '../../helpers/appointments';
import useQueueSettings from '../../hooks/queue/useQueueSettings';
import CustomerNameAge, { EXTRA_WIDTH } from '../appointments/CustomerNameAge';

interface Props {
  setSelectedAppointment: (appointment: Appointment | QueueAppointment) => void;
  setSelectedViewAppointmentId: (appointmentId: string) => void;
}

function QueueTable({
  setSelectedAppointment,
  setSelectedViewAppointmentId,
}: Props) {
  const asyncForm = useAwaitedForm();
  const { user } = useUser<LoadedUser>();
  const { openLoadingModal, closeLoadingModal } = useLoadingModal();
  const { openCheckout } = useCheckoutForm();
  const { queue, isLoading } = useGetQueue();
  const { data: waiversData } = useGetWaivers();
  const hasWaiversFeature = useFeatureGuard(Feature.WAIVERS);
  const showWaivers =
    hasWaiversFeature &&
    !!waiversData?.some(waiver => waiver.status === 'PUBLISHED');

  const { mutateAsync: removeFromQueueFn } = useRemoveFromQueue();

  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const queueSettings = useQueueSettings();
  const userCanManage = isUserAdminOrManager(user);
  const ManageIcon = userCanManage ? SettingsOutlinedIcon : LinkIcon;
  const manageText = userCanManage ? 'Walk-in Queue Settings' : 'Check-in Link';

  const onRemoveFromQueue = React.useCallback(
    async (appointmentId: string) => {
      const appointment = queue?.find(a => a.appointmentId === appointmentId);
      if (!appointment) return;

      removeFromQueue({
        appointment,
        asyncForm,
        loadingModal: {
          open: openLoadingModal,
          close: closeLoadingModal,
        },
        user,
        removeFromQueue: removeFromQueueFn,
      });
    },
    [
      queue,
      user,
      asyncForm,
      removeFromQueueFn,
      openLoadingModal,
      closeLoadingModal,
    ],
  );

  const data: DataFromColumns<typeof columns>[] = React.useMemo(() => {
    if (!queue) return [];

    return queue.map(appointment => {
      // Format check-in time
      const checkInTime = format(
        new Date(appointment.joinedQueueAt || appointment.createdAt),
        "M/d/yy 'at' h:mm a",
      );

      const service = getServiceLabel(
        appointment.serviceCategory,
        user.business.services?.find(s => s.serviceId === appointment.serviceId)
          ?.serviceName,
        true,
      );

      const preferredArtist = getSelectedArtistsText(
        user,
        appointment.selectedArtists,
      );

      const actions: ActionsData = {
        actions: [
          {
            label: 'View details',
            action: () => {
              setSelectedViewAppointmentId(appointment.appointmentId);
            },
          },
          {
            label: 'Assign to an artist',
            action: () => {
              setSelectedAppointment(appointment);
            },
          },
          !appointment.checkoutSessionActive && {
            label: 'Checkout',
            action: () => {
              openCheckout(appointment);
            },
          },
          {
            label: 'Remove from queue',
            action: () => {
              onRemoveFromQueue(appointment.appointmentId);
            },
          },
        ].filter(Boolean) as ActionsData['actions'],
      };

      return {
        client: {
          component: <CustomerNameAge appointment={appointment} />,
        },
        service,
        checkInTime,
        preferredArtist: preferredArtist || 'N/A',
        waiver: {
          component: (
            <WaiverStatusBadge
              appointment={appointment}
              waiverRequest={appointment.waiverRequest}
              waivers={waiversData}
            />
          ),
        },
        actions,
      };
    });
  }, [
    onRemoveFromQueue,
    openCheckout,
    queue,
    setSelectedAppointment,
    setSelectedViewAppointmentId,
    user,
    waiversData,
  ]);

  const columns: Columns = React.useMemo(
    () =>
      [
        {
          dataKey: 'client',
          label: 'Client',
          width: 'auto',
          extraWidth: EXTRA_WIDTH,
        },
        {
          dataKey: 'checkInTime',
          label: 'Check-in Time',
          width: 'auto',
        },
        {
          dataKey: 'preferredArtist',
          label: 'Preferred Artist(s)',
          width: 'auto',
          hidden: !queueSettings.collectArtistPreference,
        },
        {
          dataKey: 'waiver',
          label: 'Waiver',
          width: 185,
          hidden: !showWaivers,
        },
        {
          dataKey: 'service',
          label: 'Service',
          width: 'auto',
          hidden: !user?.business?.services?.length,
        },
        {
          dataKey: 'actions',
          label: '',
          width: 100,
          hidden: !data.length,
        },
      ] as const,
    [
      data.length,
      queueSettings.collectArtistPreference,
      showWaivers,
      user?.business?.services?.length,
    ],
  );

  if (isLoading) {
    return <PageLoading />;
  }

  return (
    <>
      <VirtualizedTable
        type="regular"
        isLoading={isLoading}
        columns={columns}
        data={data}
        emptyText="No clients in queue"
        headerContent={{
          title: 'Queue',
          subtitle: queue?.length ? `${queue.length} walk-ins` : undefined,
          action: {
            label: manageText,
            onClick: () => {
              setIsSettingsOpen(true);
            },
            icon: <ManageIcon width={16} height={16} />,
          },
        }}
        height={520}
      />
      {isSettingsOpen && (
        <QueueSettingsFormModal open onClose={() => setIsSettingsOpen(false)} />
      )}
    </>
  );
}

export default QueueTable;
